import {
  Car,
  Home,
  Dumbbell,
  Shield,
  Sun,
  Sparkles,
  Wrench,
  TreePine,
  ShieldCheck,
  Stethoscope,
  HeartPulse,
  Leaf,
  Zap,
} from 'lucide-react';
import { GroupBuy } from './types';
// Helper to keep mock deadlines relative to "now" so mock items remain current
const relativeDeadline = (days = 7) => {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
};

// Return a pseudo-random integer between min and max (inclusive)
const randomIntBetween = (min = 1, max = 7) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Returns a deadline N days from now where N is random between min and max (inclusive)
const randomRelativeDeadline = (minDays = 1, maxDays = 7) => {
  return relativeDeadline(randomIntBetween(minDays, maxDays));
};

export const mockGroupBuys: GroupBuy[] = [
  {
    id: '1',
    title: 'AutoCare Oil Change Package',
    description:
      '$54 one-time purchase per vehicle. Includes 3 full synthetic oil changes (up to 5 quarts each). Package valid for 12 months. Standard retail $129 per car - save 58% with group buying power.',
    category: 'automotive',
    type: 'service',
    regularPrice: 129,
    groupPrice: 54,
    currency: 'USD',
    minParticipants: 25,
    maxParticipants: 100,
    currentParticipants: 32,
    // keep this mock item perpetually near-term for development/testing
    // keep AutoCare package as-is (near-term by default)
    deadline: relativeDeadline(7),
    image: 'https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=800&h=600&fit=crop',
    icon: Car,
    organizer: {
      id: '1',
      name: 'Mike Thompson',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=MikeThompson',
      unit: 'A-302',
      rating: 4.8,
      completedGroupBuys: 12,
    },
    vendor: {
      name: 'AutoPro Mobile Services',
      rating: 4.7,
      totalReviews: 324,
      isVerified: true,
      businessSince: '2012',
    },
    discountTiers: [
      { participants: 15, discountPercent: 20, pricePerUnit: 103 },
      { participants: 25, discountPercent: 40, pricePerUnit: 77 },
      { participants: 50, discountPercent: 58, pricePerUnit: 54 },
      { participants: 100, discountPercent: 65, pricePerUnit: 45 },
    ],
    features: [
      '$54 one-time purchase per vehicle (standard $129)',
      'INCLUDES: 3 full synthetic oil changes (up to 5 quarts each)',
      'Package valid for 12 months from activation date',
      'Schedule services with 48-hour advance notice',
      'Premium synthetic oil: 5W-20, 5W-30, or 0W-20',
      'Services must be used within 12-month period (non-refundable)',
    ],
    testimonials: [
      {
        user: 'Sarah Chen',
        unit: 'B-105',
        comment:
          'Saved $200+ on my annual car maintenance. Service was professional and convenient!',
        rating: 5,
      },
      {
        user: 'David Park',
        unit: 'C-407',
        comment: 'Love that they come to my parking spot. No more waiting at the shop.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '1',
        name: 'Sarah C.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=SarahC',
        unit: 'B-105',
        joinedAt: '2025-01-05T14:22:00Z',
      },
      {
        id: '2',
        name: 'David P.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=DavidP',
        unit: 'C-407',
        joinedAt: '2025-01-06T09:15:00Z',
      },
      {
        id: '3',
        name: 'Lisa M.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=LisaM',
        unit: 'A-801',
        joinedAt: '2025-01-08T16:30:00Z',
      },
    ],
    terms: [
      'PRICE: $54 one-time purchase per vehicle (standard rate $129)',
      'PAYMENT: Full payment due at group buy close',
      'VALIDITY: 12-month service window starting from first oil change',
      'ACTIVATION: Requires minimum 25 vehicles to activate group buy',
      'SERVICES: 3 full synthetic oil changes per package',
      'SCHEDULING: Book services with 48-hour notice via mobile app or phone',
      'OIL TYPE: Premium synthetic only (5W-20, 5W-30, 0W-20)',
      'VEHICLE LIMIT: Standard sedans/SUVs up to 6 quarts oil capacity',
      'NON-TRANSFERABLE: Package tied to your vehicle VIN',
      'NO REFUNDS: Unused services expire after 12 months',
      'If 100+ vehicles join: Price drops to $45 per vehicle',
    ],
    nextSteps: [
      'Schedule first oil change appointment',
      'Receive service reminder notifications',
      'Track service history via mobile app',
    ],
    estimatedCompletion: '2025-12-31',
    // keep mock item active for development so it appears in active lists
    status: 'active',
    tags: ['automotive', 'maintenance', 'mobile-service', 'annual', 'oil-change'],
  },
  {
    id: '2',
    title: 'Community Dental Care',
    description:
      '$89 per person for comprehensive dental visit (exam + cleaning + x-rays). Valid for 12 months. Book at any of 3 partner dental offices within 10 miles. Standard retail $250 per visit - save 64% with group enrollment.',
    category: 'health',
    type: 'service',
    regularPrice: 250,
    groupPrice: 89,
    currency: 'USD',
    minParticipants: 50,
    maxParticipants: 200,
    currentParticipants: 78,
    // randomized near-term deadline (1-7 days) to make mock data look realistic
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=800&h=600&fit=crop',
    icon: HeartPulse,
    organizer: {
      id: '2',
      name: 'Dr. Emily Rodriguez',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=EmilyRodriguez',
      unit: 'B-204',
      rating: 5.0,
      completedGroupBuys: 8,
    },
    vendor: {
      name: 'Bright Smile Dental Group',
      rating: 4.9,
      totalReviews: 892,
      isVerified: true,
      businessSince: '2005',
    },
    discountTiers: [
      { participants: 30, discountPercent: 25, pricePerUnit: 187 },
      { participants: 50, discountPercent: 40, pricePerUnit: 150 },
      { participants: 100, discountPercent: 64, pricePerUnit: 89 },
      { participants: 200, discountPercent: 70, pricePerUnit: 75 },
    ],
    features: [
      '$89 per person per visit (standard $250)',
      'INCLUDES: Comprehensive dental exam by licensed dentist',
      'INCLUDES: Professional teeth cleaning (prophylaxis)',
      'INCLUDES: Full mouth digital x-rays (4-image bitewing set)',
      'INCLUDES: Oral cancer screening',
      'INCLUDES: Personalized oral health report & recommendations',
      'Valid for 12 months from purchase - schedule anytime',
      'Book appointments within 2 weeks at partner offices',
      'Choose from 3 dental offices within 10-mile radius',
      'Each household member needs separate voucher',
      'Kids & adults welcome (ages 3+)',
    ],
    testimonials: [
      {
        user: 'Jennifer Wu',
        unit: 'A-605',
        comment:
          'Amazing value! My whole family got cleanings for less than I used to pay for just one.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '4',
        name: 'Jennifer W.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=JenniferW',
        unit: 'A-605',
        joinedAt: '2025-01-10T10:00:00Z',
      },
      {
        id: '5',
        name: 'Tom Martinez',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=TomMartinez',
        unit: 'D-301',
        joinedAt: '2025-01-12T13:45:00Z',
      },
    ],
    terms: [
      'PRICE: $89 per person (one dental visit package per individual)',
      'PAYMENT: Full payment due at group buy close (November 5, 2025)',
      'VALIDITY: 12-month booking window from purchase date',
      'ACTIVATION: Requires minimum 50 people to activate group buy',
      'SERVICES: 1 exam + 1 cleaning + 1 x-ray set per voucher',
      'LOCATIONS: Choose from 3 partner offices (Maple Dental, Lakeside Smiles, Heritage Dentistry)',
      'SCHEDULING: Book appointments 2-14 days in advance',
      'AGE LIMIT: Ages 3+ (pediatric & adult services)',
      'FAMILY MEMBERS: Each person needs separate voucher (buy multiple)',
      'NON-TRANSFERABLE: Voucher tied to purchaser name',
      'NO REFUNDS: Unused vouchers expire after 12 months',
      'ADDITIONAL SERVICES: Discounted rates for fillings, crowns if needed',
      'If 200+ people join: Price drops to $75 per person',
    ],
    nextSteps: [
      'Select preferred dental office',
      'Schedule appointment online or by phone',
      'Receive confirmation and reminder',
    ],
    estimatedCompletion: '2025-11-30',
    status: 'active',
    tags: ['dental', 'health', 'preventive-care', 'annual', 'family'],
  },
  {
    id: '3',
    title: 'Premium Fitness Membership',
    description:
      '$299 per person for 6-month membership ($49.83/month average). Unlimited gym access + all group classes + 2 personal training sessions + pool/sauna access. Membership activates January 1, 2026. Standard retail $599 - save 50% with community enrollment.',
    category: 'fitness',
    type: 'service',
    regularPrice: 599,
    groupPrice: 299,
    currency: 'USD',
    minParticipants: 30,
    maxParticipants: 150,
    currentParticipants: 45,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&h=600&fit=crop',
    icon: Dumbbell,
    organizer: {
      id: '3',
      name: 'Coach Marcus Johnson',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=MarcusJohnson',
      unit: 'C-102',
      rating: 4.9,
      completedGroupBuys: 15,
    },
    vendor: {
      name: 'Peak Performance Fitness Center',
      rating: 4.8,
      totalReviews: 1247,
      isVerified: true,
      businessSince: '2010',
    },
    discountTiers: [
      { participants: 20, discountPercent: 30, pricePerUnit: 419 },
      { participants: 30, discountPercent: 45, pricePerUnit: 329 },
      { participants: 75, discountPercent: 50, pricePerUnit: 299 },
      { participants: 150, discountPercent: 60, pricePerUnit: 239 },
    ],
    features: [
      '$299 per person for 6 months (averages $49.83/month)',
      'INCLUDES: Unlimited gym access 24/7 (cardio, weights, machines)',
      'INCLUDES: All group fitness classes (yoga, spin, HIIT, Zumba, kickboxing)',
      'INCLUDES: 2 complimentary 1-hour personal training sessions',
      'INCLUDES: Pool, sauna, steam room, locker room access',
      'INCLUDES: One-time nutrition consultation (30 minutes)',
      'INCLUDES: Guest pass privileges (2 guests per month)',
      '6-month commitment (January 1 - June 30, 2026)',
      'State-of-the-art equipment & certified trainers',
      'Facility located 2 miles from community',
      'Can upgrade to 12-month at $499 (discounted rate available after 6 months)',
    ],
    testimonials: [
      {
        user: 'Alex Rivera',
        unit: 'E-503',
        comment:
          'Best investment in my health! The group classes are fantastic and the trainers are top-notch.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '6',
        name: 'Alex R.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=AlexR',
        unit: 'E-503',
        joinedAt: '2025-01-14T07:30:00Z',
      },
      {
        id: '7',
        name: 'Nina Patel',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=NinaPatel',
        unit: 'F-208',
        joinedAt: '2025-01-15T19:00:00Z',
      },
    ],
    terms: [
      'PRICE: $299 one-time payment per person for 6-month membership',
      'PAYMENT: Full payment due at group buy close (November 12, 2025)',
      'MEMBERSHIP PERIOD: January 1, 2026 - June 30, 2026 (fixed 6 months)',
      'ACTIVATION: Requires minimum 30 members to activate group buy',
      'ACCESS: 24/7 facility access with key fob (provided at signup)',
      'LOCATION: Peak Performance Fitness - 2 miles from community (123 Fitness Blvd)',
      'CLASSES: Unlimited group classes (see schedule for 40+ weekly classes)',
      'PERSONAL TRAINING: 2 sessions included (must use within membership period)',
      'PARKING: Free parking for members',
      'AGE REQUIREMENT: 16+ (under 18 requires parental consent)',
      'NON-TRANSFERABLE: Membership tied to your name & photo ID',
      'NO REFUNDS: Non-refundable after activation date',
      'UPGRADE OPTION: After 6 months, renew for $499/year (regular $999)',
      'If 150+ members join: Price drops to $239 per person',
    ],
    nextSteps: [
      'Complete fitness assessment',
      'Receive membership key fob',
      'Schedule orientation tour',
    ],
    estimatedCompletion: '2026-01-01',
    status: 'active',
    tags: ['fitness', 'gym', 'wellness', 'membership', 'training'],
  },
  {
    id: '4',
    title: 'Deep Cleaning Service',
    description:
      '$119 per cleaning session (2-bedroom unit, 3-4 hours). One-time deep clean includes all rooms, kitchen, bathrooms. Book within 60 days. Eco-friendly products & experienced cleaners. Standard retail $249 - save 52% with group rate.',
    category: 'home',
    type: 'service',
    regularPrice: 249,
    groupPrice: 119,
    currency: 'USD',
    minParticipants: 20,
    maxParticipants: 75,
    currentParticipants: 28,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&h=600&fit=crop',
    icon: Sparkles,
    organizer: {
      id: '4',
      name: 'Maria Santos',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=MariaSantos',
      unit: 'D-505',
      rating: 4.9,
      completedGroupBuys: 20,
    },
    vendor: {
      name: 'Sparkle & Shine Cleaning Co.',
      rating: 4.8,
      totalReviews: 567,
      isVerified: true,
      businessSince: '2015',
    },
    discountTiers: [
      { participants: 10, discountPercent: 25, pricePerUnit: 186 },
      { participants: 20, discountPercent: 40, pricePerUnit: 149 },
      { participants: 40, discountPercent: 52, pricePerUnit: 119 },
      { participants: 75, discountPercent: 60, pricePerUnit: 99 },
    ],
    features: [
      '$119 per cleaning session for 2-bedroom unit (standard $249)',
      'INCLUDES: 3-4 hour deep cleaning by 2 professional cleaners',
      'INCLUDES: Kitchen (appliances inside/out, counters, cabinets, sink, floors)',
      'INCLUDES: Bathrooms (tubs, showers, tiles, toilets, sinks, mirrors)',
      'INCLUDES: Living areas (dusting, vacuuming, mopping all floors)',
      'INCLUDES: Bedrooms (dusting, vacuuming, changing linens if provided)',
      'INCLUDES: Baseboards, light fixtures, ceiling fans',
      'All eco-friendly & pet-safe cleaning products',
      'Team of 2 experienced cleaners',
      'Satisfaction guaranteed - free re-clean within 24 hours if not satisfied',
      'Pricing: 1-bed ($99), 2-bed ($119), 3-bed ($149), 4-bed ($189)',
    ],
    testimonials: [
      {
        user: 'Karen White',
        unit: 'G-402',
        comment: 'My apartment has never looked this good! Worth every penny.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '8',
        name: 'Karen W.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=KarenW',
        unit: 'G-402',
        joinedAt: '2025-01-18T11:00:00Z',
      },
      {
        id: '9',
        name: 'James Lee',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=JamesLee',
        unit: 'H-101',
        joinedAt: '2025-01-19T15:30:00Z',
      },
    ],
    terms: [
      'PRICE: $119 per deep cleaning session for 2-bedroom unit',
      'UNIT PRICING: 1-bed ($99), 2-bed ($119), 3-bed ($149), 4-bed ($189)',
      'PAYMENT: Full payment due at group buy close (November 18, 2025)',
      'BOOKING WINDOW: Schedule service within 60 days of purchase',
      'ACTIVATION: Requires minimum 20 units to activate group buy',
      'SERVICE DURATION: 3-4 hours (2-bedroom unit)',
      'TEAM SIZE: 2 professional cleaners per session',
      'AVAILABILITY: Weekdays only (Monday-Friday, 9am-5pm)',
      'SUPPLIES: All cleaning products & equipment provided',
      'PETS: Pet-friendly cleaning products (pets must be secured during service)',
      'ONE-TIME SERVICE: This is for one deep cleaning session per voucher',
      'NON-REFUNDABLE: Must be used within 60 days (no extensions)',
      'ADDITIONAL SERVICES: Add-ons available (windows $40, oven $30, fridge $25)',
      'If 75+ units join: Price drops to $99 per 2-bedroom cleaning',
    ],
    nextSteps: [
      'Schedule cleaning date and time',
      'Receive reminder 24 hours before',
      'Cleaning team arrives with supplies',
    ],
    estimatedCompletion: '2026-01-31',
    status: 'active',
    tags: ['cleaning', 'home', 'deep-clean', 'eco-friendly', 'professional'],
  },
  {
    id: '5',
    title: 'Smart Home Security System',
    description:
      '$329 one-time purchase for complete security system (2 cameras, 4 sensors, 2 motion detectors, control panel) + first 3 months monitoring FREE. Optional monitoring $29.99/month after trial. DIY installation. Standard retail $599 - save 45% with group buy.',
    category: 'security',
    type: 'product',
    regularPrice: 599,
    groupPrice: 329,
    currency: 'USD',
    minParticipants: 15,
    maxParticipants: 60,
    currentParticipants: 22,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&h=600&fit=crop',
    icon: ShieldCheck,
    organizer: {
      id: '5',
      name: 'Tech-Savvy Sam',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=SamTech',
      unit: 'E-707',
      rating: 4.7,
      completedGroupBuys: 9,
    },
    vendor: {
      name: 'SecureHome Technologies',
      rating: 4.6,
      totalReviews: 423,
      isVerified: true,
      businessSince: '2016',
    },
    discountTiers: [
      { participants: 10, discountPercent: 30, pricePerUnit: 419 },
      { participants: 15, discountPercent: 45, pricePerUnit: 329 },
      { participants: 30, discountPercent: 55, pricePerUnit: 269 },
      { participants: 60, discountPercent: 65, pricePerUnit: 209 },
    ],
    features: [
      '$329 one-time purchase per household (standard $599)',
      'INCLUDES: 2 HD cameras (1080p, indoor/outdoor, night vision)',
      'INCLUDES: 4 door/window entry sensors (battery-powered)',
      'INCLUDES: 2 motion detectors (pet-immune up to 40 lbs)',
      'INCLUDES: Control panel with 7" touchscreen display',
      'INCLUDES: Mobile app (iOS/Android) with live streaming',
      'INCLUDES: First 3 months professional monitoring FREE ($89.97 value)',
      'DIY installation (30-60 minutes, video tutorials included)',
      '2-year manufacturer warranty on all hardware',
      'Cloud storage: 7-day rolling video history included',
      'Optional monitoring: $29.99/month after free trial (no contracts)',
    ],
    testimonials: [
      {
        user: 'Robert Chen',
        unit: 'B-903',
        comment: 'Easy to install and works great. Feel much safer now!',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '10',
        name: 'Robert C.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=RobertC',
        unit: 'B-903',
        joinedAt: '2025-01-20T14:15:00Z',
      },
      {
        id: '11',
        name: 'Diana Foster',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=DianaFoster',
        unit: 'F-506',
        joinedAt: '2025-01-21T09:30:00Z',
      },
    ],
    terms: [
      'PRICE: $329 one-time purchase per household for complete system',
      'PAYMENT: Full payment due at group buy close (November 22, 2025)',
      'DELIVERY: Ships within 7-10 business days after group buy closes',
      'ACTIVATION: Requires minimum 15 households to activate group buy',
      'HARDWARE: 2 cameras, 4 sensors, 2 motion detectors, 1 control panel',
      'INSTALLATION: DIY (30-60 minutes, includes mounting hardware & instructions)',
      'WARRANTY: 2-year manufacturer warranty on all equipment',
      'MONITORING TRIAL: First 3 months FREE (24/7 professional monitoring)',
      'MONITORING PRICING: After trial, $29.99/month (cancel anytime, no contract)',
      'MONITORING FEATURES: Emergency dispatch, smartphone alerts, video verification',
      'CLOUD STORAGE: 7-day rolling video history included with monitoring',
      'EXPANSION: Additional cameras $79 each, sensors $19 each (discounted)',
      'INTERNET REQUIRED: Needs WiFi connection (min 5 Mbps upload speed)',
      'If 60+ households join: Price drops to $209 per system',
    ],
    nextSteps: [
      'Equipment ships to your address',
      'Follow installation video guide',
      'Activate monitoring service',
    ],
    estimatedCompletion: '2025-12-15',
    status: 'active',
    tags: ['security', 'smart-home', 'diy', 'monitoring', 'cameras'],
  },
  {
    id: '6',
    title: 'Residential Solar Panel Installation',
    description:
      '$9,500 one-time installation per home for complete 6kW solar system. Includes 18 high-efficiency panels, inverter, monitoring, professional installation, 25-year warranty. Reduces electric bills 60-70%. Eligible for 30% federal tax credit ($2,850). Standard retail $15,000 - save 37% with group rate.',
    category: 'energy',
    type: 'service',
    regularPrice: 15000,
    groupPrice: 9500,
    currency: 'USD',
    minParticipants: 10,
    maxParticipants: 40,
    currentParticipants: 14,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&h=600&fit=crop',
    icon: Zap,
    organizer: {
      id: '6',
      name: 'Green Energy Gary',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=GaryGreen',
      unit: 'F-401',
      rating: 5.0,
      completedGroupBuys: 6,
    },
    vendor: {
      name: 'SunPower Solutions Inc.',
      rating: 4.9,
      totalReviews: 289,
      isVerified: true,
      businessSince: '2011',
    },
    discountTiers: [
      { participants: 5, discountPercent: 20, pricePerUnit: 12000 },
      { participants: 10, discountPercent: 37, pricePerUnit: 9500 },
      { participants: 20, discountPercent: 45, pricePerUnit: 8250 },
      { participants: 40, discountPercent: 50, pricePerUnit: 7500 },
    ],
    features: [
      '$9,500 one-time installation per home (standard $15,000)',
      'INCLUDES: Complete 6kW solar panel system (18 panels, 335W each)',
      'INCLUDES: High-efficiency monocrystalline solar panels (22% efficiency)',
      'INCLUDES: 6kW string inverter (converts DC to AC power)',
      'INCLUDES: WiFi monitoring system (track energy production via app)',
      'INCLUDES: Professional installation by certified electricians (2-day install)',
      'INCLUDES: All racking, wiring, electrical permits, inspection fees',
      '25-year panel warranty + 10-year inverter warranty + 10-year workmanship warranty',
      'Federal tax credit eligible: 30% of $9,500 = $2,850 rebate',
      'Average energy savings: 60-70% reduction in electric bills',
      'ROI: 6-8 years average payback period',
      'Free home energy assessment before installation',
    ],
    testimonials: [
      {
        user: 'Patricia Green',
        unit: 'H-602',
        comment: 'My electric bills are down 65%! Installation was smooth and professional.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '12',
        name: 'Patricia G.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=PatriciaG',
        unit: 'H-602',
        joinedAt: '2025-01-22T10:00:00Z',
      },
      {
        id: '13',
        name: 'Michael Torres',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=MichaelTorres',
        unit: 'A-104',
        joinedAt: '2025-01-23T13:20:00Z',
      },
    ],
    terms: [
      'PRICE: $9,500 one-time installation per home (6kW system)',
      'PAYMENT: $1,000 deposit at group buy close, balance due before installation',
      'INSTALLATION: Scheduled within 90 days of group buy activation',
      'ACTIVATION: Requires minimum 10 homes to activate group buy',
      'SYSTEM SIZE: 6kW (18 panels @ 335W each, produces ~8,500 kWh/year)',
      'INSTALLATION TIME: 2-day installation per home by 2-person crew',
      'ROOF REQUIREMENTS: Structurally sound, south/west/east facing, min 250 sq ft',
      'HOA/BUILDING APPROVAL: Required before installation (vendor assists with paperwork)',
      'PERMITS & INSPECTIONS: Included (electrical permit, building inspection, utility interconnection)',
      'FEDERAL TAX CREDIT: 30% of $9,500 = $2,850 (consult tax advisor)',
      'WARRANTY: 25-year panels, 10-year inverter, 10-year workmanship',
      'MAINTENANCE: Annual inspection recommended (vendor offers $150/year service plan)',
      'NET METERING: Eligible (excess energy credits from utility company)',
      'FINANCING: Available through SunPower (3.99% APR, no down payment option)',
      'If 40+ homes join: Price drops to $7,500 per installation',
    ],
    nextSteps: [
      'Free home energy assessment',
      'Obtain HOA/building approval',
      'Schedule installation date',
    ],
    estimatedCompletion: '2026-03-01',
    status: 'active',
    tags: ['solar', 'energy', 'green', 'savings', 'installation'],
  },
  {
    id: '7',
    title: 'Home Maintenance Package',
    description:
      '$349 per year for comprehensive home maintenance (2 HVAC services, plumbing inspection, electrical inspection, 4 hours handyman time). Services valid for 12 months. Priority scheduling. Standard retail $699 - save 50% with group enrollment.',
    category: 'home',
    type: 'service',
    regularPrice: 699,
    groupPrice: 349,
    currency: 'USD',
    minParticipants: 25,
    maxParticipants: 100,
    currentParticipants: 39,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1581578949510-fa7315c4c350?w=800&h=600&fit=crop',
    icon: Wrench,
    organizer: {
      id: '7',
      name: 'Handy Henry',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=HenryHandy',
      unit: 'G-203',
      rating: 4.8,
      completedGroupBuys: 18,
    },
    vendor: {
      name: 'HomeCare Experts LLC',
      rating: 4.7,
      totalReviews: 512,
      isVerified: true,
      businessSince: '2009',
    },
    discountTiers: [
      { participants: 15, discountPercent: 30, pricePerUnit: 489 },
      { participants: 25, discountPercent: 45, pricePerUnit: 384 },
      { participants: 50, discountPercent: 50, pricePerUnit: 349 },
      { participants: 100, discountPercent: 60, pricePerUnit: 279 },
    ],
    features: [
      '$349 per household for 12-month maintenance package (standard $699)',
      'INCLUDES: 2 HVAC service visits (spring tune-up + fall tune-up)',
      'HVAC Details: Filter replacement, coil cleaning, refrigerant check, thermostat calibration',
      'INCLUDES: Annual plumbing inspection (30-point check)',
      'Plumbing Details: Check for leaks, test water pressure, inspect fixtures, drain inspection',
      'INCLUDES: Electrical safety inspection (40-point check)',
      'Electrical Details: Panel inspection, outlet testing, GFCI testing, surge protection check',
      'INCLUDES: 4 hours of handyman services (can split into 2-hour visits)',
      'Handyman: Minor repairs, fixture installation, caulking, drywall patching, etc.',
      'Priority scheduling for package members (1-week notice)',
      '20% discount on additional services beyond package',
      'Monday-Saturday availability (8am-6pm)',
    ],
    testimonials: [
      {
        user: 'Betty Wilson',
        unit: 'C-508',
        comment: 'Such peace of mind knowing everything is taken care of. Great value!',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '14',
        name: 'Betty W.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=BettyW',
        unit: 'C-508',
        joinedAt: '2025-01-24T12:00:00Z',
      },
    ],
    terms: [
      'PRICE: $349 one-time payment per household for 12-month package',
      'PAYMENT: Full payment due at group buy close (December 5, 2025)',
      'VALIDITY: 12-month service window starting January 1, 2026',
      'ACTIVATION: Requires minimum 25 households to activate group buy',
      'HVAC SERVICES: 2 visits (schedule in March-April & September-October)',
      'PLUMBING INSPECTION: 1 annual visit (schedule anytime within 12 months)',
      'ELECTRICAL INSPECTION: 1 annual visit (schedule anytime within 12 months)',
      'HANDYMAN TIME: 4 hours total (can split into two 2-hour visits)',
      'SCHEDULING: Book services with 1-week advance notice',
      'AVAILABILITY: Monday-Saturday, 8am-6pm',
      'ADDITIONAL SERVICES: 20% member discount on services beyond package',
      'NON-TRANSFERABLE: Package tied to your unit address',
      'NO REFUNDS: Unused services expire after 12 months',
      'EMERGENCY SERVICES: Not included (call regular rates apply)',
      'If 100+ households join: Price drops to $279 per household',
    ],
    nextSteps: [
      'Receive service package confirmation',
      'Schedule first HVAC service visit',
      'Download service tracking app',
    ],
    estimatedCompletion: '2026-01-01',
    status: 'active',
    tags: ['home', 'maintenance', 'hvac', 'plumbing', 'electrical', 'handyman'],
  },
  {
    id: '8',
    title: 'Landscape & Power Wash Services',
    description:
      '$219 per season (6 months) for complete exterior care: 6 lawn mowings, 2 hedge trimmings, 2 yard cleanups, 2 power washes, spring flowers, mulch delivery. Services April-September. Standard retail $449 - save 51% with group rate.',
    category: 'home',
    type: 'service',
    regularPrice: 449,
    groupPrice: 219,
    currency: 'USD',
    minParticipants: 20,
    maxParticipants: 80,
    currentParticipants: 31,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&h=600&fit=crop',
    icon: Leaf,
    organizer: {
      id: '8',
      name: 'Landscaper Larry',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=LarryLandscape',
      unit: 'H-305',
      rating: 4.9,
      completedGroupBuys: 14,
    },
    vendor: {
      name: 'GreenScape Pro Services',
      rating: 4.8,
      totalReviews: 398,
      isVerified: true,
      businessSince: '2013',
    },
    discountTiers: [
      { participants: 10, discountPercent: 25, pricePerUnit: 336 },
      { participants: 20, discountPercent: 40, pricePerUnit: 269 },
      { participants: 40, discountPercent: 51, pricePerUnit: 219 },
      { participants: 80, discountPercent: 60, pricePerUnit: 179 },
    ],
    features: [
      '$219 per household for 6-month exterior care package (April-September)',
      'INCLUDES: 6 monthly lawn mowings + edging (April, May, June, July, August, September)',
      'INCLUDES: 2 hedge/bush trimming sessions (spring & summer)',
      'INCLUDES: Spring yard cleanup (April: remove winter debris, aerate, overseed)',
      'INCLUDES: Fall yard cleanup (September: leaf removal, gutter clearing)',
      'INCLUDES: 2 power washing sessions (driveway, walkway, patio - spring & late summer)',
      'INCLUDES: Spring flower planting (1 flat of seasonal flowers + mulch bed prep)',
      'INCLUDES: 1 mulch delivery & application (3 cubic yards, applied to beds)',
      'Standard residential lot coverage (up to 1/4 acre)',
      'Professional 2-person crew for each visit',
      'All equipment & supplies included',
      'Weather-dependent scheduling (will reschedule if raining)',
    ],
    testimonials: [
      {
        user: 'George Taylor',
        unit: 'D-607',
        comment: 'My yard has never looked better. Great crew, always on time!',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '15',
        name: 'George T.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=GeorgeT',
        unit: 'D-607',
        joinedAt: '2025-01-25T08:00:00Z',
      },
    ],
    terms: [
      'PRICE: $219 per household for 6-month seasonal package',
      'PAYMENT: Full payment due at group buy close (December 10, 2025)',
      'SERVICE PERIOD: April 1 - September 30, 2026 (6 months)',
      'ACTIVATION: Requires minimum 20 households to activate group buy',
      'LAWN MOWING: 6 visits (once per month, includes mowing, edging, blowing)',
      'HEDGE TRIMMING: 2 visits (May & July, up to 50 linear feet of hedges)',
      'YARD CLEANUPS: 2 visits (April spring cleanup, September fall cleanup)',
      'POWER WASHING: 2 sessions (April & August, up to 800 sq ft combined)',
      'FLOWERS: 1 flat (18 plants) + bed prep in April',
      'MULCH: 3 cubic yards delivered & spread (covers ~150 sq ft)',
      'LOT SIZE: Standard residential lot up to 1/4 acre (10,890 sq ft)',
      'SCHEDULING: Services scheduled with 3-day weather window (crew notifies day before)',
      'CREW SIZE: 2-person professional team',
      'ADDITIONAL SERVICES: Member discounts available (fertilizing, tree trimming, etc.)',
      'If 80+ households join: Price drops to $179 per household',
    ],
    nextSteps: [
      'Crew surveys property in March',
      'Service schedule confirmed',
      'Receive notifications before each visit',
    ],
    estimatedCompletion: '2026-04-01',
    status: 'active',
    tags: ['landscape', 'lawn', 'power-wash', 'seasonal', 'exterior', 'yard'],
  },
  {
    id: '9',
    title: 'Community Security Patrol Service',
    description:
      '$89/month per household for 12 months. Professional 24/7 armed security patrols covering all 8 community buildings. 2 patrol officers on rotating shifts. Pricing requires minimum 150 households to activate - the more neighbors who join, the lower the monthly cost per home.',
    category: 'security',
    type: 'service',
    regularPrice: 200,
    groupPrice: 89,
    currency: 'USD',
    minParticipants: 150,
    maxParticipants: 300,
    currentParticipants: 87,
    deadline: randomRelativeDeadline(1, 7),
    image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop',
    icon: ShieldCheck,
    organizer: {
      id: '9',
      name: 'Captain Steve Richardson',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=SteveRichardson',
      unit: 'A-801',
      rating: 4.9,
      completedGroupBuys: 4,
    },
    vendor: {
      name: 'Guardian Community Security',
      rating: 4.9,
      totalReviews: 678,
      isVerified: true,
      businessSince: '2008',
    },
    discountTiers: [
      { participants: 100, discountPercent: 30, pricePerUnit: 140 },
      { participants: 150, discountPercent: 55, pricePerUnit: 89 },
      { participants: 250, discountPercent: 65, pricePerUnit: 69 },
    ],
    features: [
      '$89/month per household (billed monthly)',
      '24/7 armed security patrols - 2 officers per shift',
      'Covers all 8 buildings + parking lots + common areas',
      '3 patrol vehicles with GPS tracking',
      'Under 5-minute incident response time',
      'Monthly security reports emailed to all participants',
      'Free vacation home monitoring (notify patrol when away)',
      'Direct coordination with local police department',
      'Mobile app with real-time patrol locations',
      'Resident alert system for suspicious activity',
      '12-month commitment with monthly auto-pay',
    ],
    testimonials: [
      {
        user: 'Rebecca Martinez',
        unit: 'B-705',
        comment: 'Peace of mind is priceless. Great to see our community come together for safety.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '15',
        name: 'Rebecca M.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=RebeccaM',
        unit: 'B-705',
        joinedAt: '2025-01-12T11:30:00Z',
      },
      {
        id: '16',
        name: 'Dr. Paul Kim',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=PaulKim',
        unit: 'C-402',
        joinedAt: '2025-01-15T08:45:00Z',
      },
    ],
    terms: [
      'PRICE: $89/month per household (standard rate $200/month)',
      'COMMITMENT: 12-month service agreement starting January 1, 2026',
      'BILLING: Monthly auto-debit from checking/credit card',
      'ACTIVATION: Requires minimum 150 households to activate service',
      'CANCELLATION: 60-day notice required, prorated refund available',
      'COVERAGE AREA: All 8 buildings (A-H), parking structures, common areas, perimeter',
      'SERVICE HOURS: 24/7/365 continuous patrol coverage',
      'OFFICERS: 2 licensed, armed security professionals per 8-hour shift',
      'VEHICLES: 3 marked patrol vehicles with emergency lights',
      'INSURANCE: $5M liability coverage included in service',
      'If 250+ households join: Price drops to $69/month per household',
    ],
    nextSteps: [
      'Guardian assesses community security needs',
      'Patrol routes and schedules established',
      'Mobile app setup and resident onboarding',
    ],
    estimatedCompletion: '2025-04-01',
    status: 'active',
    tags: ['security', 'patrol', 'safety', 'community', 'annual', 'peace-of-mind'],
  },
  {
    id: '10',
    title: 'VIP Medical Emergency Service',
    description:
      '$1,450 per year per individual for priority medical care. Skip ER wait times, 24/7 concierge hotline, same-day specialist appointments, annual health screening. Family plans available (+$450 per additional member). Standard retail $2,400 per year - save 40% with group enrollment.',
    category: 'medical',
    type: 'service',
    regularPrice: 2400,
    groupPrice: 1450,
    currency: 'USD',
    minParticipants: 25,
    maxParticipants: 50,
    currentParticipants: 12,
    deadline: relativeDeadline(7),
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop',
    icon: Stethoscope,
    organizer: {
      id: '10',
      name: 'Dr. Sarah Mitchell',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=SarahMitchell',
      unit: 'A-1002',
      rating: 4.8,
      completedGroupBuys: 2,
    },
    vendor: {
      name: 'Premier Healthcare Concierge',
      rating: 4.9,
      totalReviews: 234,
      isVerified: true,
      businessSince: '2019',
    },
    discountTiers: [
      { participants: 20, discountPercent: 25, pricePerUnit: 1800 },
      { participants: 25, discountPercent: 40, pricePerUnit: 1450 },
      { participants: 40, discountPercent: 50, pricePerUnit: 1200 },
    ],
    features: [
      '$1,450 per person per year for VIP medical membership (standard $2,400)',
      'INCLUDES: Priority ER access (skip standard triage wait times)',
      'INCLUDES: 24/7 concierge medical hotline (direct physician access)',
      'INCLUDES: Same-day specialist appointments (cardiology, orthopedics, etc.)',
      'INCLUDES: Annual comprehensive health screening (full panel bloodwork, EKG, imaging)',
      'INCLUDES: Medical advocacy & care coordination services',
      'INCLUDES: Travel medical emergency coverage (nationwide network)',
      'INCLUDES: Prescription assistance & medication management',
      'INCLUDES: Second opinion consultations (major diagnosis/surgery)',
      'INCLUDES: Hospital room upgrades (subject to availability)',
      'Average ER wait time: Under 15 minutes for members',
      'Family pricing: +$450 per additional household member',
    ],
    testimonials: [
      {
        user: 'Robert Chen',
        unit: 'D-403',
        comment:
          'Worth every penny! Got immediate care when I needed it most. Saved $950 annually.',
        rating: 5,
      },
    ],
    participants: [
      {
        id: '17',
        name: 'Robert C.',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=RobertC',
        unit: 'D-403',
        joinedAt: '2025-01-10T13:20:00Z',
      },
      {
        id: '18',
        name: 'Linda Davis',
        avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=LindaDavis',
        unit: 'B-901',
        joinedAt: '2025-01-14T16:15:00Z',
      },
    ],
    terms: [
      'PRICE: $1,450 per person per year (standard rate $2,400)',
      'FAMILY PRICING: First member $1,450, each additional member +$450',
      'PAYMENT: Full payment due at group buy close (December 15, 2025)',
      'MEMBERSHIP PERIOD: 12 months starting January 1, 2026',
      'ACTIVATION: Requires minimum 25 individuals to activate group buy',
      'PRIORITY ER: Expedited triage at 12 partner hospitals in metro area',
      'CONCIERGE HOTLINE: 24/7 direct physician access (board-certified doctors)',
      'SPECIALIST APPOINTMENTS: Same-day or next-day booking (vs 2-4 week standard wait)',
      'HEALTH SCREENING: Annual physical + comprehensive labs (valued at $800+)',
      'MEDICAL ADVOCACY: Help navigating insurance, billing, care coordination',
      'TRAVEL COVERAGE: Emergency care access at 500+ partner facilities nationwide',
      'NOT INSURANCE: Supplements existing health insurance, does not replace',
      'AGE REQUIREMENT: All ages accepted (pediatric & adult services)',
      'RENEWALS: After 12 months, renew at $1,800/year (regular $2,400)',
      'If 40+ individuals join: Price drops to $1,200 per person per year',
    ],
    nextSteps: [
      'Medical intake and health assessment',
      'Membership cards and app access provided',
      'Orientation to concierge services',
    ],
    estimatedCompletion: '2025-03-10',
    status: 'active',
    tags: ['medical', 'vip', 'emergency', 'concierge', 'healthcare', 'priority'],
  },
];
